import React from 'react';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { DeltaBadge } from './oi-delta';
import { SignalBadge, ConfidenceDots } from './oi-signals-ui';
import { scoreToSignalTier, signalTierColor } from './oi-signals';

function MiniSparkline({ data }: { data?: number[] }) {
  if (!data?.length) return null;
  const chartData = data.map((v, i) => ({ i, v }));
  return (
    <div style={{ height: 40, marginTop: 8 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line type="monotone" dataKey="v" stroke="var(--oi-signal-blue)" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function HeroKpiCard({
  label,
  value,
  score,
  sublabel,
  statusTier,
}: {
  label: string;
  value: React.ReactNode;
  score?: number;
  sublabel?: string;
  statusTier?: ReturnType<typeof scoreToSignalTier>;
}) {
  const tier = statusTier ?? (score != null ? scoreToSignalTier(score) : undefined);
  return (
    <div className="oi-kpi-card oi-kpi-card--t1">
      {tier ? (
        <div className="oi-kpi-card__accent" style={{ background: signalTierColor(tier) }} />
      ) : null}
      <div className="oi-kpi-card__overline">{label}</div>
      <div className="oi-kpi-card__value oi-kpi-card__value--hero oi-mono-value">{value}</div>
      {tier ? <SignalBadge tier={tier} /> : null}
      {sublabel ? <div className="oi-kpi-card__footer">{sublabel}</div> : null}
    </div>
  );
}

export function PrimaryKpiCard({
  label,
  value,
  deltaPct,
  trend,
  target,
  confidence,
  updatedAt,
  sparkline,
  onClick,
  accentTier,
}: {
  label: string;
  value: React.ReactNode;
  deltaPct?: number | null;
  trend?: 'up' | 'down' | 'flat';
  target?: string;
  confidence?: number;
  updatedAt?: string;
  sparkline?: number[];
  onClick?: () => void;
  accentTier?: ReturnType<typeof scoreToSignalTier>;
}) {
  return (
    <div
      className={`oi-kpi-card oi-kpi-card--t2${onClick ? ' oi-kpi-card--clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {accentTier ? (
        <div className="oi-kpi-card__accent" style={{ background: signalTierColor(accentTier) }} />
      ) : null}
      <div className="oi-kpi-card__overline">{label}</div>
      <div className="oi-kpi-card__value oi-kpi-card__value--primary oi-mono-value">{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        {(deltaPct != null || trend) && <DeltaBadge deltaPct={deltaPct} trend={trend} />}
        {confidence != null ? <ConfidenceDots value={confidence} /> : null}
      </div>
      {target ? <div className="oi-kpi-card__footer">Target: {target}</div> : null}
      <MiniSparkline data={sparkline} />
      {updatedAt ? <div className="oi-kpi-card__footer">Updated {updatedAt}</div> : null}
    </div>
  );
}

export function SecondaryKpiCard({
  label,
  value,
  statusDot,
}: {
  label: string;
  value: React.ReactNode;
  statusDot?: string;
}) {
  return (
    <div className="oi-kpi-card oi-kpi-card--t3">
      <div className="oi-kpi-card__overline">{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {statusDot ? (
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusDot }} />
        ) : null}
        <div className="oi-kpi-card__value oi-kpi-card__value--secondary oi-mono-value">{value}</div>
      </div>
    </div>
  );
}

export function MicroKpiCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="oi-kpi-card oi-kpi-card--t4">
      <div className="oi-kpi-card__overline">{label}</div>
      <div className="oi-kpi-card__value oi-kpi-card__value--micro oi-mono-value">{value}</div>
    </div>
  );
}
